/**
 * The offline entry queue (FR-STF-09).
 *
 * A teacher records a meal in a corridor with no signal and must not lose it. Every care entry the staff
 * app creates goes into this queue first and is sent from here, so there is exactly one path out of the
 * app and exactly one place that knows whether something has been delivered.
 *
 * Three rules the requirement imposes, and how each is met:
 *
 *  - *No entry is lost silently.* The queue is written to `localStorage` on every change, so a closed tab,
 *    a flat battery or a browser reaping the page leaves the entries intact.
 *  - *The user is told when items are pending.* The queue publishes its state; the shell renders a count
 *    and the pending screen lists them.
 *  - *Entries synchronise automatically.* A flush runs on load, whenever the browser reports it is online
 *    again, and after each successful send.
 *
 * Delivery is at-least-once, and each entry carries a client-generated `id` that the send function passes
 * to the API as an idempotency key — a response lost on the way back must not become a second meal.
 */

import { storage } from '@daghfal/core';

export type QueueEntryKind =
  | 'meal' | 'drink' | 'sleep' | 'activity' | 'mood' | 'diaper' | 'photo' | 'note';

export interface QueueEntry {
  /** Generated on the device. Doubles as the idempotency key when the entry is sent. */
  id: string;
  kind: QueueEntryKind;
  /** The children this entry applies to — several at once, for a whole-class entry (FR-STF-05). */
  childIds: number[];
  classId: number | null;
  /** When the teacher recorded it, not when it was sent. */
  recordedAt: string;
  payload: Record<string, unknown>;
  attempts: number;
  /** Why the last attempt failed: technical, for diagnosis, and never shown (it may be a status code). */
  lastError: string | null;
  /** The API's own words for why it refused the entry, for the pending screen; absent for anything else. */
  refusal?: string | null;
  /** The children it is held for because they were not checked in at its time (NR-9); the rest were recorded. */
  waitingFor?: number[] | null;
  /** Set once the API has refused the entry outright; it stops being retried and waits for a human. */
  rejected: boolean;
}

/** What a send attempt concluded. */
export type SendOutcome =
  /** Delivered. The entry leaves the queue. */
  | { status: 'sent' }
  /** Not now — offline, a timeout, a 5xx. The entry stays and is retried. */
  | { status: 'retry'; reason: string }
  /**
   * The API refused it. Retrying would refuse it again, so it is held for the teacher to deal with. `message` is
   * the API's own words, the only reason the pending screen shows; `childIds`, where given, are the children it
   * still waits for, the rest having been recorded (NR-9, a child not checked in).
   */
  | { status: 'rejected'; reason: string; message?: string; childIds?: number[] }
  /**
   * Not attempted at all — there is nowhere to send it yet. Unlike `retry` this does not count against
   * the attempt budget, so an entry waiting on an unshipped endpoint is never aged into `rejected`.
   */
  | { status: 'deferred'; reason: string };

export type Sender = (entry: QueueEntry) => Promise<SendOutcome>;

const STORAGE_KEY = 'daghfal.staff.queue';

export class EntryQueue {
  private entries: QueueEntry[] = [];
  private listeners = new Set<() => void>();
  /**
   * What delivers an entry, and the attempts before one is set aside as rejected: enough to ride out a bad
   * morning, not so many that a doomed entry retries for ever (`offlineQueue.maximumAttempts` from
   * `useLimits`). Null until `start`, and again once stopped.
   */
  private delivery: { send: Sender; maximumAttempts: number } | null = null;
  private cleanup: (() => void) | null = null;

  /** The pass currently running, so that a second caller awaits it rather than starting a rival one. */
  private inFlight: Promise<void> | null = null;
  /** Set when work arrives mid-pass; the runner then makes another pass rather than missing it. */
  private repeat = false;

  constructor() {
    this.entries = read();
  }

  /** Installs the function that actually delivers an entry, and starts the automatic flushing. */
  start(sender: Sender, maximumAttempts: number): () => void {
    this.delivery = { send: sender, maximumAttempts };

    const onOnline = () => { void this.flush(); };
    globalThis.addEventListener?.('online', onOnline);

    // Anything left from a previous session goes out as soon as the app opens.
    void this.flush();

    this.cleanup = () => {
      globalThis.removeEventListener?.('online', onOnline);
      this.delivery = null;
    };
    return this.cleanup;
  }

  /** Adds an entry and tries to send it immediately. Returns at once — the caller never waits on a send. */
  enqueue(entry: Omit<QueueEntry, 'id' | 'attempts' | 'lastError' | 'rejected'>): QueueEntry {
    const created: QueueEntry = {
      ...entry,
      id: newId(),
      attempts: 0,
      lastError: null,
      rejected: false,
    };
    this.entries = [...this.entries, created];
    this.commit();
    void this.flush();
    return created;
  }

  /** Discards an entry. Used only for one the API has rejected and the teacher has chosen to drop. */
  discard(id: string): void {
    this.entries = this.entries.filter((entry) => entry.id !== id);
    this.commit();
  }

  /** Puts a rejected entry back in the running — after the teacher has fixed whatever was wrong. */
  revive(id: string): void {
    this.entries = this.entries.map((entry) =>
      entry.id === id ? { ...entry, rejected: false, attempts: 0, lastError: null, refusal: null, waitingFor: null } : entry);
    this.commit();
    void this.flush();
  }

  /**
   * Sends everything that is waiting, oldest first.
   *
   * Awaiting this waits for the work, not merely for the call. A second caller while a pass is running
   * joins that pass rather than starting a rival one — so the teacher's "try sending now" button reports
   * a real outcome, and two entries recorded a second apart never race each other to the same endpoint.
   */
  flush(): Promise<void> {
    if (this.inFlight) {
      // Something arrived while a pass was already under way. Rather than skip it until the next
      // trigger, the runner is told to make another pass once this one is done.
      this.repeat = true;
      return this.inFlight;
    }
    this.inFlight = this.run().finally(() => { this.inFlight = null; });
    return this.inFlight;
  }

  private async run(): Promise<void> {
    do {
      this.repeat = false;
      await this.pass();
    } while (this.repeat);
  }

  /**
   * One pass over the queue.
   *
   * Serial rather than parallel, and in order: entries for the same child often depend on each other's
   * ordering (a sleep that started before it ended), and a teacher on a weak connection is better served
   * by one request at a time than by six competing for it.
   */
  private async pass(): Promise<void> {
    if (!this.delivery) return;
    if (globalThis.navigator?.onLine === false) return;

    for (const entry of [...this.entries]) {
      if (entry.rejected) continue;
      const delivery = this.delivery;
      if (!delivery) break;

      // A sender is contracted to resolve with an outcome, never to throw. If one breaks that contract
      // the entry is treated as retryable rather than allowed to escape: every flush is started with
      // `void`, so an exception here would surface as an unhandled rejection and, worse, abandon the
      // rest of the pass — losing entries silently is the one thing FR-STF-09 forbids.
      let outcome: SendOutcome;
      try {
        outcome = await delivery.send(entry);
      } catch (cause) {
        outcome = { status: 'retry', reason: cause instanceof Error ? cause.message : 'send-failed' };
      }

      if (outcome.status === 'sent') {
        this.entries = this.entries.filter((candidate) => candidate.id !== entry.id);
        this.commit();
        continue;
      }

      if (outcome.status === 'deferred') {
        // Recorded so the pending screen can say what it is waiting on, but the attempt count is left
        // alone: nothing was actually tried.
        this.entries = this.entries.map((candidate) =>
          candidate.id === entry.id ? { ...candidate, lastError: outcome.reason } : candidate);
        this.commit();
        break;
      }

      const attempts = entry.attempts + 1;
      // An entry that has failed this many times is no longer a connectivity problem. It is held
      // rather than dropped, because dropping it is exactly what FR-STF-09 forbids.
      const rejected = outcome.status === 'rejected' || attempts >= delivery.maximumAttempts;

      const refused = outcome.status === 'rejected' ? outcome : null;
      this.entries = this.entries.map((candidate) =>
        candidate.id === entry.id
          ? {
            ...candidate, attempts, lastError: outcome.reason, rejected,
            refusal: refused?.message ?? null,
            waitingFor: refused?.childIds ?? null,
            childIds: refused?.childIds ?? candidate.childIds,
          }
          : candidate);
      this.commit();

      // A retryable failure almost certainly applies to every entry behind it too, so the pass stops
      // here rather than hammering a connection that is not there.
      if (outcome.status === 'retry') break;
    }
  }

  // ---- Subscription, for useSyncExternalStore ----------------------------------------------------------

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  /** Returns the same array identity until something actually changes, as the store contract requires. */
  snapshot = (): QueueEntry[] => this.entries;

  private commit(): void {
    write(this.entries);
    for (const listener of this.listeners) listener();
  }
}

function read(): QueueEntry[] {
  // Corrupt storage is not worth crashing the app a teacher is standing in front of. The entries are gone
  // either way; starting empty at least keeps the app usable.
  const parsed = storage.readJson<unknown>(STORAGE_KEY);
  return Array.isArray(parsed) ? (parsed as QueueEntry[]) : [];
}

function write(entries: QueueEntry[]): void {
  // Storage full or blocked: the entries survive in memory for this session, and the pending count still
  // shows, so the teacher is not told everything is fine when it is not.
  storage.writeJson(STORAGE_KEY, entries);
}

function newId(): string {
  // `randomUUID` needs a secure context, which the app always has in production but not necessarily on a
  // plain-http device used for testing.
  return globalThis.crypto?.randomUUID?.()
    ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
